import { ArrowLeftIcon } from '@radix-ui/react-icons';
import React from 'react';

import { dateShort2 } from '../../../assets/contants/dates-fns';
import { formatPlainDate, plainDateFromEpochDay } from '../../../core/services/plain-date';
import styles from './offscreen-indicator.module.scss';

import type { Row } from '../types';

type OffscreenIndicatorProps = {
  row: Row;
  side: 'start' | 'end';
  onScrollTo: (row: Row) => void;
};

/**
 * The chip that pins to a viewport edge when a row's bar has scrolled out of view. `side` is
 * supplied by the caller — derived from `offscreenSideOf`, a pure function — rather than decided
 * here, which is the whole point of Task 13: the old version wrote `justifySelf` /
 * `flexDirection` / an SVG `scaleX` onto the DOM node from inside an IntersectionObserver
 * callback, and none of that was testable.
 */
export const OffscreenIndicator: React.FC<OffscreenIndicatorProps> = ({
  row,
  side,
  onScrollTo,
}) => {
  if (!row.span) return null;

  const start = formatPlainDate(plainDateFromEpochDay(row.span.startDay), dateShort2);
  const end = formatPlainDate(plainDateFromEpochDay(row.span.endDay), dateShort2);
  const label = row.span.startDay === row.span.endDay ? start : `${start} - ${end}`;

  return (
    <button
      type="button"
      data-testid="offscreen"
      data-side={side}
      className={styles['indicator']}
      onClick={() => onScrollTo(row)}
      aria-label={`Scroll to ${row.item.name}, ${label}`}
    >
      <ArrowLeftIcon />

      <span className={styles['content']}>
        {side === 'end' ? (
          <span className={`${styles['text']} ${styles['name']}`}>{row.item.name}</span>
        ) : null}
        <span className={styles['text']}>{label}</span>
      </span>
    </button>
  );
};
